import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Trash2,
  User,
  Save,
  Edit2,
  AlertTriangle,
  Camera,
  ListIcon, GripVertical
} from "lucide-react";
// Add these imports at the top
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function Profile({ userId }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("personal"); // personal, programs
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // User must move 8px before dragging starts
      },
    })
  );  // --- STATE ---
  const [athlete, setAthlete] = useState(null);
  const [originalAthlete, setOriginalAthlete] = useState(null);

  const [followedPrograms, setFollowedPrograms] = useState([]);

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // --- RELATIONSHIP STATE ---
  const [teamMemberships, setTeamMemberships] = useState([]);
  const [originalTeamMemberships, setOriginalTeamMemberships] = useState([]);
  const [removedTeamMembershipIds, setRemovedTeamMembershipIds] = useState([]);
  const [unfollowTarget, setUnfollowTarget] = useState(null);


  const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
  const quotePostgrestText = (value) => {
    if (value === null || value === undefined) return '""';
    const escaped = String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return `"${escaped}"`;
  };

  // -----------------------------
  // 1. Load Data
  // -----------------------------
  useEffect(() => {
    async function loadData() {
      try {
        const { data: profile } = await supabase
          .from("users")
          .select("id, full_name, email, phone_number, gender, grade, age, user_school, height_feet, height_inches, weight, home_location, user_lat, user_lng, bio, role, profile_img")
          .eq("id", userId)
          .single();

        setAthlete(profile ?? null);
        setOriginalAthlete(profile ?? null);

      } catch (err) {
        console.error("Error loading profile data:", err);
      }
    }
    if (userId) loadData();
  }, [userId]);

  // Prefetch programs + team memberships after initial paint
  useEffect(() => {
    if (!athlete || !userId) return;
    let cancelled = false;

    const prefetchProgramsAndTeams = async () => {
      try {
        const [{ data: followedData }, { data: userTeamData, error: userTeamErr }] = await Promise.all([
          supabase
            .from('user_program_follows')
            .select('*, programs(*)')
            .eq('user_id', userId)
            .order('order_index', { ascending: true }),
          supabase
            .from('rostered_users')
            .select(`
              id,
              team_id,
              teams (
                id,
                name,
                program_id,
                programs (
                  id,
                  name,
                  sport
                )
              )
            `)
            .eq('user_id', userId)
            .order('created_at', { ascending: true })
        ]);

        if (!cancelled) {
          setFollowedPrograms(followedData ?? []);
        }

        let teamData = null;
        if (userTeamErr) {
          throw userTeamErr;
        } else if ((userTeamData || []).length > 0) {
          teamData = userTeamData;
        } else {
          const rosteredEmail = normalizeEmail(athlete.email);
          if (rosteredEmail) {
            const { data: emailTeamData, error: emailTeamErr } = await supabase
              .from('rostered_users')
              .select(`
                id,
                team_id,
                teams (
                  id,
                  name,
                  program_id,
                  programs (
                    id,
                    name,
                    sport
                  )
                )
              `)
              .eq('teamsnap_email', rosteredEmail)
              .order('created_at', { ascending: true });
            if (emailTeamErr) throw emailTeamErr;
            teamData = emailTeamData || [];
          } else {
            teamData = [];
          }
        }

        if (!cancelled) {
          const memberships = teamData || [];
          setTeamMemberships(memberships);
          setOriginalTeamMemberships(memberships);
        }
      } catch (error) {
        console.error("Error prefetching programs:", error);
      }
    };

    const timeoutId = setTimeout(prefetchProgramsAndTeams, 0);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [athlete, userId]);

  // -----------------------------
  // 3. Handlers
  // -----------------------------

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) navigate("/login");
  };

  const handleImageUpload = async (event) => {
    try {
      setUploadingImage(true);
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAthlete((prev) => ({ ...prev, profile_img: publicUrl }));

    } catch (error) {
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const triggerFileInput = () => {
    if (editing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = followedPrograms.findIndex((fp) => fp.id === active.id);
    const newIndex = followedPrograms.findIndex((fp) => fp.id === over.id);

    // 1. Get the new array order
    const movedArray = arrayMove(followedPrograms, oldIndex, newIndex);

    // 2. CRITICAL: Update the order_index property in the local state objects
    // If you don't do this, your .sort() logic in the JSX will revert the UI.
    const updatedState = movedArray.map((item, index) => ({
      ...item,
      order_index: index + 1,
    }));

    // 3. Update UI state with the new indexes
    setFollowedPrograms(updatedState);

    // 4. Prepare the clean updates for Supabase
    const updates = updatedState.map((item) => ({
      id: item.id,
      order_index: item.order_index,
    }));

    try {
      const { error } = await supabase
        .from('user_program_follows')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;
    } catch (err) {
      console.error("Error saving new order:", err);
      // Optionally: fetchFollowedPrograms() here to reset to DB state on error
    }
  };

  // --- SAVE HANDLER (Multi-Row Support) ---
  const handleSave = async () => {
    setLoading(true);
    try {
      const rosteredEmail = normalizeEmail(athlete?.email);
      // 1. Update basic info
      const { error: userError } = await supabase.from("users").update({
        full_name: athlete.full_name,
        email: athlete.email,
        phone_number: athlete.phone_number,
        height_feet: athlete.height_feet,
        height_inches: athlete.height_inches,
        weight: athlete.weight,
        user_school: athlete.user_school,
        grade: athlete.grade,
        age: athlete.age,
        bio: athlete.bio,
        gender: athlete.gender,
        home_location: athlete.home_location,
        user_lat: athlete.user_lat,
        user_lng: athlete.user_lng,
        profile_img: athlete.profile_img
      }).eq("id", userId);

      if (userError) throw userError;

      // 2. Save team membership removals
      const visibleMemberships = teamMemberships.filter(
        (row) => !removedTeamMembershipIds.includes(row.id)
      );

      if (removedTeamMembershipIds.length > 0) {
        let deleteQuery = supabase
          .from("rostered_users")
          .delete()
          .in("id", removedTeamMembershipIds);

        if (rosteredEmail) {
          deleteQuery = deleteQuery.or(`user_id.eq.${userId},teamsnap_email.eq.${quotePostgrestText(rosteredEmail)}`);
        } else {
          deleteQuery = deleteQuery.eq("user_id", userId);
        }

        const { error: deleteError } = await deleteQuery;
        if (deleteError) throw deleteError;
      }

      setOriginalAthlete({ ...athlete });
      setOriginalTeamMemberships(visibleMemberships);
      setTeamMemberships(visibleMemberships);
      setRemovedTeamMembershipIds([]);
      setEditing(false);

    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save changes. Please check inputs.");
    } finally {
      setLoading(false);
    }
  };

  // --- UNFOLLOW LOGIC ---
  const initiateUnfollow = (program) => setUnfollowTarget(program);
  const cancelUnfollow = () => setUnfollowTarget(null);
  const confirmUnfollow = async () => {
    if (!unfollowTarget) return;
    try {
      const { error } = await supabase.from("user_program_follows").delete().eq("id", unfollowTarget.id);
      if (error) throw error;
      setFollowedPrograms((prev) => prev.filter((fp) => fp.id !== unfollowTarget.id));
      setUnfollowTarget(null);
    } catch (err) { console.error(err); alert("Failed to unfollow program."); }
  };

  const handleCancel = () => {
    setAthlete(originalAthlete);
    setTeamMemberships(originalTeamMemberships);
    setRemovedTeamMembershipIds([]);
    setEditing(false);
  };

  const handleMarkLeaveTeam = (rowId) => {
    if (!editing) return;
    setRemovedTeamMembershipIds((prev) => (
      prev.includes(rowId) ? prev : [...prev, rowId]
    ));
  };

  const handleUndoLeaveTeam = (rowId) => {
    if (!editing) return;
    setRemovedTeamMembershipIds((prev) => prev.filter((id) => id !== rowId));
  };

  if (!athlete) return <div className="p-8 text-center text-gray-500">Loading profile...</div>;
  function SortableProgramItem({ fp, onUnfollow }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: fp.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 50 : 'auto',
    };

    return (
      <li
        ref={setNodeRef}
        style={style}
        className={`flex justify-between items-center p-3 rounded-lg border transition-all ${isDragging ? "bg-blue-50 border-blue-300 shadow-md opacity-50" : "bg-slate-50 border-slate-100"
          }`}
      >
        <div className="flex items-center gap-3 flex-grow">
          {/* Drag Handle */}
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 p-1">
            <GripVertical size={18} />
          </div>
          <div>
            <p className="font-semibold text-sm text-slate-800">{fp.programs?.name}</p>
            <p className="text-xs text-slate-500 uppercase">{fp.programs?.sport}</p>
          </div>
        </div>
        <button
          onClick={() => onUnfollow(fp)}
          className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-full transition"
        >
          <Trash2 size={16} />
        </button>
      </li>
    );
  }

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div className="max-w-6xl mx-auto p-4 pb-24 md:pb-8 font-sans text-slate-800 relative">

      {/* --- 1. HEADER (STAYS PERSISTENT) --- */}
      <div className="flex flex-col md:flex-row items-center gap-6 mb-8 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="relative flex-shrink-0 group">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="hidden"
            disabled={!editing || uploadingImage}
          />
          <div
            onClick={triggerFileInput}
            className={`relative w-28 h-28 md:w-32 md:h-32 rounded-full border-4 border-white shadow-md bg-gray-200 overflow-hidden ${editing ? 'cursor-pointer' : ''}`}
          >
            <img
              src={athlete.profile_img || `https://ui-avatars.com/api/?name=${encodeURIComponent(athlete.full_name || "User")}&background=random`}
              alt="Profile"
              className={`w-full h-full object-cover transition duration-300 ${uploadingImage ? 'opacity-50' : 'opacity-100'}`}
            />
            {editing && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="text-white" size={24} />
              </div>
            )}
          </div>
          {editing && <div className="absolute bottom-1 right-1 bg-blue-600 text-white rounded-full p-2 shadow-md"><Edit2 size={14} /></div>}
        </div>

        <div className="flex-1 text-center md:text-left">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{athlete.full_name || "Unnamed Athlete"}</h1>
          <p className="text-blue-600 font-semibold flex items-center justify-center md:justify-start gap-2">
            {athlete.role} <span className="text-slate-300">|</span>
            <span className="text-slate-500 text-sm font-medium">{athlete.user_school || "No School Listed"}</span>
          </p>

          <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
            {editing ? (
              <>
                <button onClick={handleSave} disabled={loading} className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition">
                  <Save size={16} /> {loading ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={handleCancel} className="px-5 py-2 bg-slate-100 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-200 transition">Cancel</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-lg shadow-sm hover:bg-slate-50 transition">
                <Edit2 size={16} /> Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- 2. MAIN LAYOUT (SIDEBAR + CONTENT) --- */}
      <div className="flex flex-col md:flex-row gap-8">

        {/* --- SIDEBAR NAVIGATION --- */}
        <div className="w-full md:w-64 flex-shrink-0">
          <nav className="flex md:flex-col overflow-x-auto md:overflow-visible gap-2 p-1.5 bg-slate-100/50 rounded-2xl md:bg-transparent">
            {[
              { id: "personal", label: "Personal Info", icon: <User size={18} /> },
              { id: "programs", label: "My Programs", icon: <ListIcon size={18} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap
                ${activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-200 translate-x-1"
                    : "text-slate-500 hover:bg-white hover:text-slate-900"}`}
              >
                <div className="flex items-center gap-3">
                  {tab.icon}
                  {tab.label}
                </div>
                {tab.badge > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{tab.badge}</span>}
              </button>
            ))}
          </nav>

          <div className="hidden md:block mt-10 pt-6 border-t border-slate-200">
            <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-red-500 font-bold text-sm transition-colors">
              <LogOut size={16} /> Log Out
            </button>
          </div>
        </div>

        {/* --- CONTENT AREA --- */}
        <div className="flex-1 min-w-0">

          {/* TAB: PERSONAL DETAILS */}
          {activeTab === "personal" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <h2 className="text-lg font-bold mb-6 text-slate-900 border-b pb-2 flex items-center gap-2">
                  <User size={20} className="text-blue-500" /> Personal Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {[
                    { label: "Full Name", name: "full_name" },
                    { label: "Email", name: "email" },
                    { label: "Phone Number", name: "phone_number" },
                    { label: "Gender", name: "gender" },
                    { label: "Grade", name: "grade" },
                    { label: "Age", name: "age" },
                    { label: "School", name: "user_school" },
                    { label: "Height (ft)", name: "height_feet" },
                    { label: "Height (in)", name: "height_inches" },
                    { label: "Weight (lbs)", name: "weight" },
                    { label: "Home Address", name: "home_location" },
                  ].map((f) => (
                    <div key={f.name}>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 block">{f.label}</label>
                      {editing ? (
                        <input
                          name={f.name}
                          value={athlete[f.name] ?? ""}
                          onChange={(e) => setAthlete(prev => ({ ...prev, [e.target.name]: e.target.value }))}
                          className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      ) : (
                        <p className="text-sm text-slate-800 font-semibold py-1">{athlete[f.name] || "--"}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <h2 className="text-lg font-bold mb-4 text-slate-900 border-b pb-2">Bio</h2>
                {editing ? (
                  <textarea
                    name="bio"
                    rows={4}
                    value={athlete.bio ?? ""}
                    onChange={(e) => setAthlete(prev => ({ ...prev, bio: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="Tell coaches about your journey..."
                  />
                ) : (
                  <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{athlete.bio || "No bio info provided."}</p>
                )}
              </div>
            </div>
          )}

          {/* TAB: PROGRAMS */}
          {activeTab === "programs" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              {/* FOLLOWED PROGRAMS LIST */}
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                  <h2 className="text-lg font-bold text-slate-900">Team Memberships</h2>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    {teamMemberships.filter((row) => !removedTeamMembershipIds.includes(row.id)).length} Active
                  </span>
                </div>

                {teamMemberships.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-slate-400 text-sm italic">You are not currently rostered on a team.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamMemberships.map((row) => {
                      const markedForRemoval = removedTeamMembershipIds.includes(row.id);
                      const programName = row?.teams?.programs?.name || "Program";
                      const teamName = row?.teams?.name || "Team";
                      return (
                        <div
                          key={row.id}
                          className={`rounded-xl border p-4 transition ${markedForRemoval ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-100"}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                            <div>
                              <p className="font-semibold text-sm text-slate-900">{programName}</p>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">{teamName}</p>
                            </div>
                            {editing ? (
                              markedForRemoval ? (
                                <button
                                  type="button"
                                  onClick={() => handleUndoLeaveTeam(row.id)}
                                  className="text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                >
                                  Undo Leave
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleMarkLeaveTeam(row.id)}
                                  className="text-xs font-bold px-3 py-1.5 rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50"
                                >
                                  Leave Team
                                </button>
                              )
                            ) : null}
                          </div>

                          {markedForRemoval ? (
                            <p className="text-sm text-red-700 font-medium">This team will be removed when you save changes.</p>
                          ) : (
                            <div />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* FOLLOWED PROGRAMS LIST */}
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                  <h2 className="text-lg font-bold text-slate-900">Program Priority</h2>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Drag to Reorder</span>
                </div>

                {followedPrograms.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-slate-400 text-sm italic">You haven't followed any programs yet.</p>
                  </div>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={followedPrograms.map(fp => fp.id)} strategy={verticalListSortingStrategy}>
                      <ul className="space-y-3">
                        {[...followedPrograms]
                          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                          .map((fp) => (
                            <SortableProgramItem key={fp.id} fp={fp} onUnfollow={initiateUnfollow} />
                          ))}
                      </ul>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- FOOTER / LOGOUT (MOBILE ONLY) --- */}
      <div className="md:hidden mt-12 mb-8">
        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 p-4 bg-white border border-red-100 text-red-600 rounded-2xl font-bold text-sm shadow-sm">
          <LogOut size={18} /> Log Out
        </button>
      </div>

      {/* --- MODALS --- */}
      {unfollowTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 text-center border border-slate-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Unfollow Program?</h3>
            <p className="text-slate-500 text-sm mb-8 leading-relaxed">
              Stopping your following of <span className="font-bold text-slate-800">{unfollowTarget.programs?.name}</span> will remove it from your dashboard.
            </p>
            <div className="flex gap-3">
              <button onClick={cancelUnfollow} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition">Go Back</button>
              <button onClick={confirmUnfollow} className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition shadow-lg shadow-red-100">Unfollow</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
